import React, { useEffect, useState } from "react";
import { NavLink, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useLang } from "../context/LanguageContext";
import { FirstLoginPassword } from "./FirstLoginPassword";
import {
  LayoutDashboard, Wrench, CalendarDays, FileText, Building2, Users,
  LogOut, Languages, HardHat, MapPin, Inbox, ShoppingCart, Wallet, Banknote, Boxes, Timer, Receipt, ClipboardList, UserCog, Truck, Briefcase, Sliders, Package, ShieldCheck, Menu, X, TrendingUp, Layers, UsersRound, HelpCircle,
} from "lucide-react";
import NotificationBell from "./NotificationBell";

const navByRole = {
  admin: ["dashboard", "inbox", "tickets", "projects", "schedule", "crew", "timeclock", "payroll", "hr", "map", "estimates", "receivables", "purchasing", "material_requests", "materials", "material_trends", "suppliers", "subcontractors", "equipment", "payments", "financials", "finance", "clients", "properties", "technicians", "permissions", "settings", "help"],
  manager: ["dashboard", "inbox", "tickets", "projects", "schedule", "crew", "timeclock", "payroll", "hr", "map", "estimates", "receivables", "purchasing", "material_requests", "materials", "material_trends", "suppliers", "subcontractors", "equipment", "payments", "financials", "finance", "clients", "properties", "technicians", "settings", "help"],
  estimator: ["dashboard", "tickets", "projects", "estimates", "subcontractors", "equipment", "help"],
  pm: ["dashboard", "tickets", "projects", "schedule", "crew", "map", "estimates", "material_requests", "subcontractors", "equipment", "help"],
  technician: ["dashboard", "tickets", "schedule", "timeclock", "map", "material_requests", "help"],
  purchasing: ["dashboard", "purchasing", "material_requests", "materials", "material_trends", "suppliers", "equipment", "estimates", "help"],
  director: ["dashboard", "material_requests", "tickets", "projects", "financials", "finance", "receivables", "help"],
  client: ["dashboard", "tickets", "projects", "estimates", "properties", "help"],
  analista: ["dashboard", "tickets", "projects", "estimates", "receivables", "financials", "finance", "material_requests", "material_trends", "properties", "clients", "help"],
  financeiro: ["dashboard", "projects", "receivables", "financials", "finance", "payroll", "hr", "material_requests", "material_trends", "purchasing", "clients", "help"],
  rh: ["dashboard", "hr", "technicians", "payroll", "timeclock", "crew", "help"],
  controller: ["dashboard", "financials", "finance", "payroll", "receivables", "purchasing", "material_requests", "material_trends", "materials", "suppliers", "subcontractors", "projects", "clients", "properties", "help"],
  subcontractor: ["dashboard", "tickets", "help"],
};

const icons = {
  dashboard: LayoutDashboard, tickets: Wrench, schedule: CalendarDays,
  estimates: FileText, properties: Building2, technicians: Users, map: MapPin, inbox: Inbox,
  purchasing: ShoppingCart, financials: Wallet, finance: Wallet, subcontractors: HardHat, payments: Banknote, materials: Boxes, timeclock: Timer, receivables: Receipt, payroll: ClipboardList, crew: UserCog, suppliers: Truck, clients: Briefcase, settings: Sliders, material_requests: Package, permissions: ShieldCheck, material_trends: TrendingUp, projects: Layers, equipment: Truck, hr: UsersRound, help: HelpCircle,
};
const paths = {
  dashboard: "/", tickets: "/tickets", schedule: "/schedule",
  estimates: "/estimates", properties: "/properties", technicians: "/team", map: "/map", inbox: "/inbox",
  purchasing: "/purchasing", financials: "/financials", finance: "/finance", subcontractors: "/subcontractors", equipment: "/equipment", payments: "/payments", materials: "/materials", timeclock: "/timeclock", receivables: "/receivables", payroll: "/payroll", crew: "/crew", suppliers: "/suppliers", clients: "/clients", settings: "/settings", material_requests: "/material-requests", permissions: "/permissions", material_trends: "/material-trends", projects: "/projects", hr: "/hr", help: "/help",
};

// Sidebar items that are gated by the permissions matrix (in addition to the
// per-role whitelist above). If a permission is defined here for an item, it
// is only rendered when `can(module, "view")` is true — so admins editing
// /permissions can widen or narrow visibility without a code change.
const permByItem = {
  crew: "crew",
  financials: "financials",
  finance: "financials",
};

export const Layout = ({ children }) => {
  const { user, logout, can } = useAuth();
  const { t, lang, toggle } = useLang();
  const navigate = useNavigate();
  const location = useLocation();
  const rawItems = navByRole[user.role] || ["dashboard"];
  // For any sidebar item that is gated by the permissions matrix, its visibility
  // is driven purely by `can()` — so admins can widen (add) or narrow (hide)
  // access via /permissions without a code change. Non-gated items still fall
  // back to the per-role whitelist.
  const items = (() => {
    const set = new Set(rawItems.filter((key) => !permByItem[key]));
    Object.entries(permByItem).forEach(([key, mod]) => {
      if (can(mod, "view")) set.add(key);
    });
    // Preserve original ordering: keep positions from navByRole where possible,
    // and append any permission-granted extras at the end of their role's order.
    const ordered = rawItems.filter((k) => set.has(k));
    Object.keys(permByItem).forEach((k) => {
      if (set.has(k) && !ordered.includes(k)) ordered.push(k);
    });
    return ordered;
  })();
  const [drawerOpen, setDrawerOpen] = useState(false);

  // Close the mobile drawer whenever the route changes
  useEffect(() => { setDrawerOpen(false); }, [location.pathname]);
  // Lock body scroll while the drawer is open on mobile
  useEffect(() => {
    document.body.style.overflow = drawerOpen ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [drawerOpen]);

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  const renderSidebar = (compact = false) => (
    <>
      <div className={`px-6 py-6 border-b border-slate-800 flex items-center justify-between`}>
        <div>
          <img src="/brand/jmo_logo.png" alt="JMO Group" className="h-9 w-auto" />
          <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500 mt-2.5">{t("appName")}</div>
        </div>
        {compact && (
          <button
            data-testid="mobile-drawer-close"
            onClick={() => setDrawerOpen(false)}
            className="p-1.5 rounded-md text-slate-400 hover:text-white hover:bg-slate-800"
            aria-label="Close menu"
          >
            <X className="w-5 h-5" />
          </button>
        )}
      </div>
      <nav className="flex-1 min-h-0 overflow-y-auto px-3 py-4 space-y-1">
        {items.map((key) => {
          const Icon = icons[key];
          return (
            <NavLink
              key={key}
              to={paths[key]}
              end={paths[key] === "/"}
              data-testid={`nav-${key}`}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors duration-150 ${
                  isActive ? "bg-slate-800 text-white" : "text-slate-400 hover:text-white hover:bg-slate-800/60"
                }`
              }
            >
              <Icon className="w-5 h-5" />
              {t(key)}
            </NavLink>
          );
        })}
      </nav>
      <div className="px-3 py-4 border-t border-slate-800 space-y-1">
        <button
          data-testid={compact ? "lang-toggle-mobile" : "lang-toggle"}
          onClick={toggle}
          className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium text-slate-400 hover:text-white hover:bg-slate-800/60 transition-colors"
        >
          <Languages className="w-5 h-5" />
          {lang === "en" ? "Português" : "English"}
        </button>
        <button
          data-testid={compact ? "logout-btn-mobile" : "logout-btn"}
          onClick={handleLogout}
          className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium text-slate-400 hover:text-white hover:bg-slate-800/60 transition-colors"
        >
          <LogOut className="w-5 h-5" />
          {t("logout")}
        </button>
      </div>
    </>
  );

  return (
    <div className="min-h-screen flex bg-slate-50">
      {/* Desktop sidebar (md+) */}
      <aside className="hidden md:flex w-64 flex-col bg-slate-900 text-slate-300 fixed h-full">
        {renderSidebar(false)}
      </aside>

      {/* Mobile drawer (portrait) — backdrop + slide-in panel */}
      {drawerOpen && (
        <button
          data-testid="mobile-drawer-backdrop"
          aria-label="Close menu"
          onClick={() => setDrawerOpen(false)}
          className="md:hidden fixed inset-0 z-40 bg-black/50 backdrop-blur-sm animate-in fade-in"
        />
      )}
      <aside
        data-testid="mobile-drawer"
        className={`md:hidden fixed z-50 top-0 left-0 h-full w-72 max-w-[85vw] bg-slate-900 text-slate-300 flex flex-col shadow-2xl transform transition-transform duration-300 ease-out ${
          drawerOpen ? "translate-x-0" : "-translate-x-full"
        }`}
      >
        {renderSidebar(true)}
      </aside>

      {/* Main */}
      <div className="flex-1 md:ml-64 flex flex-col min-w-0">
        <header className="sticky top-0 z-20 bg-white/90 backdrop-blur border-b border-slate-200 px-3 md:px-8 py-3 md:py-3.5 flex items-center justify-between gap-2">
          <div className="flex items-center gap-2 min-w-0">
            <button
              data-testid="mobile-menu-btn"
              onClick={() => setDrawerOpen(true)}
              className="md:hidden p-2 -ml-1 rounded-md text-slate-600 hover:bg-slate-100"
              aria-label="Open menu"
            >
              <Menu className="w-5 h-5" />
            </button>
            <div className="text-xs md:text-sm text-slate-500 truncate">
              {t("welcome")},{" "}
              <span className="font-semibold text-slate-900">{user.name}</span>
              <span className="ml-2 hidden sm:inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium bg-blue-50 text-blue-700 capitalize">
                {user.role}
              </span>
            </div>
          </div>
          <div className="flex items-center gap-1 md:gap-2 shrink-0">
            <NotificationBell />
            <button
              data-testid="lang-toggle-top"
              onClick={toggle}
              className="md:hidden flex items-center gap-1 text-[11px] font-medium text-slate-600 border border-slate-200 rounded-full px-2.5 py-1.5"
              aria-label="Toggle language"
            >
              <Languages className="w-3.5 h-3.5" />
              {lang === "en" ? "PT" : "EN"}
            </button>
          </div>
        </header>
        <main className="flex-1 p-3 md:p-8">{children}</main>
        <FirstLoginPassword />
      </div>
    </div>
  );
};
